import { runInAction } from 'mobx';
import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { debounce } from '@gem/utils';

import Tasks from 'APP/Tasks';
import { ICalendarAccount } from 'APP/model/calendar/calendar.types';
import Entities from 'STORE';

interface ICalendarRealtimeSubscriberPresenter {
  isSubscribeRequested: boolean;
  isUnsubscribeRequested: boolean;
  subscribedToPushAccounts: Set<string>;
  isCalendarInstances: boolean;
  activeCalendarAccounts: ICalendarAccount[];
  calendarInstancesCount: number;
  updateIsCalendarInstances(count: number): void;
  subscribeToPush(): Promise<void>;
  unsubscribeFromPush(): Promise<void>;
}

export function useCalendarRealtimeSubscriberPresenter(): void {
  const presenter = useLocalObservable<ICalendarRealtimeSubscriberPresenter>(() => ({
    isSubscribeRequested: false,
    isUnsubscribeRequested: false,
    subscribedToPushAccounts: new Set<string>([]),
    isCalendarInstances: false,

    get activeCalendarAccounts(): ICalendarAccount[] {
      return Entities.calendar.activeCalendarAccounts;
    },

    get calendarInstancesCount(): number {
      return Entities.calendar.calendarInstancesCount;
    },

    updateIsCalendarInstances: debounce((count: number) => {
      presenter.isCalendarInstances = count > 0;
    }, 1000),

    async subscribeToPush(): Promise<void> {
      if (
        !!presenter.subscribedToPushAccounts.size &&
        presenter.activeCalendarAccounts.every((account) =>
          presenter.subscribedToPushAccounts.has(account.providerEmail)
        )
      ) {
        return;
      }

      presenter.isSubscribeRequested = true;

      const isSubscribed = await Tasks.calendar.subscribeToCalendarPush({
        accounts: presenter.activeCalendarAccounts,
      });

      if (isSubscribed) {
        for (const account of presenter.activeCalendarAccounts) {
          presenter.subscribedToPushAccounts.add(account.providerEmail);
        }
      }

      runInAction(() => {
        presenter.isSubscribeRequested = false;
      });
    },

    async unsubscribeFromPush(): Promise<void> {
      if (!presenter.subscribedToPushAccounts.size) {
        return;
      }
      presenter.isUnsubscribeRequested = true;
      const isUnsubscribed = await Tasks.calendar.unsubscribeFromCalendarPush({
        accounts: presenter.activeCalendarAccounts,
      });

      if (isUnsubscribed) {
        for (const account of presenter.activeCalendarAccounts) {
          presenter.subscribedToPushAccounts.delete(account.providerEmail);
        }
      }

      presenter.isUnsubscribeRequested = false;
    },
  }));

  useEffect(() => {
    if (!presenter.activeCalendarAccounts.length) {
      return;
    }
    if (presenter.isCalendarInstances && !presenter.isUnsubscribeRequested) {
      presenter.subscribeToPush();
    }
  }, [
    presenter.isCalendarInstances,
    presenter.isUnsubscribeRequested,
    presenter.activeCalendarAccounts,
  ]);

  useEffect(() => {
    if (!presenter.isCalendarInstances && !presenter.isSubscribeRequested) {
      presenter.unsubscribeFromPush();
    }
  }, [presenter.isCalendarInstances, presenter.isSubscribeRequested]);

  useEffect(() => {
    presenter.updateIsCalendarInstances(presenter.calendarInstancesCount);
  }, [presenter.calendarInstancesCount]);

  // Unsubscribe on onmount
  useEffect(() => {
    return () => {
      presenter.unsubscribeFromPush();
    };
  }, []);

  return;
}
